import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  AlertTriangle,
  Crosshair,
  Shield,
  CheckSquare,
  Truck,
  Loader2,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Clock,
  Link2,
  Award
} from 'lucide-react';
import { Link } from 'react-router-dom';

const GGRCChainPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [chainData, setChainData] = useState({ chain: [], stats: {} });
  const [expandedRisks, setExpandedRisks] = useState({});

  useEffect(() => {
    fetchChainData();
  }, []);

  const fetchChainData = async () => {
    try {
      const response = await api.get('/ggrc/chain');
      setChainData(response.data);
    } catch (error) {
      console.error('Error fetching chain data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRiskExpand = (riskId) => {
    setExpandedRisks(prev => ({
      ...prev,
      [riskId]: !prev[riskId]
    }));
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500',
      high: 'bg-orange-500',
      medium: 'bg-yellow-500',
      low: 'bg-green-500'
    };
    return colors[severity] || 'bg-neutral-400';
  };

  const getSeverityBadge = (severity) => {
    const config = {
      critical: { label: 'Kritiek', class: 'bg-red-50 text-red-700 border-red-200' },
      high: { label: 'Hoog', class: 'bg-orange-50 text-orange-700 border-orange-200' },
      medium: { label: 'Medium', class: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
      low: { label: 'Laag', class: 'bg-green-50 text-green-700 border-green-200' }
    };
    const { label, class: className } = config[severity] || config.medium;
    return <Badge className={className}>{label}</Badge>;
  };

  const getStatusIcon = (status) => {
    const icons = {
      open: <AlertCircle className="h-4 w-4 text-red-500" />,
      mitigating: <Clock className="h-4 w-4 text-yellow-500" />,
      accepted: <CheckCircle className="h-4 w-4 text-blue-500" />,
      closed: <CheckCircle className="h-4 w-4 text-green-500" />
    };
    return icons[status] || icons.open;
  };

  const getMeasureStatusBadge = (status) => {
    const config = {
      planned: { label: 'Gepland', class: 'bg-neutral-100 text-neutral-600' },
      in_progress: { label: 'Bezig', class: 'bg-blue-50 text-blue-700' },
      implemented: { label: 'Klaar', class: 'bg-green-50 text-green-700' },
      verified: { label: 'Geverifieerd', class: 'bg-emerald-50 text-emerald-700' }
    };
    const { label, class: className } = config[status] || config.planned;
    return <Badge className={`${className} border-transparent text-xs`}>{label}</Badge>;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" strokeWidth={1.5} />
        </div>
      </AdminLayout>
    );
  }

  const { chain = [], stats = {} } = chainData || {};
  
  // Safe stats access
  const safeStats = {
    open_vulnerabilities: stats?.open_vulnerabilities || 0,
    total_threats: stats?.total_threats || 0,
    open_risks: stats?.open_risks || 0,
    implemented_measures: stats?.implemented_measures || 0,
    available_suppliers: stats?.available_suppliers || 0
  };

  // Calculate Top 3 suppliers based on frequency in the chain
  const supplierCounts = {};
  chain.forEach(item => {
    item.suppliers?.forEach(supplier => {
      if (supplierCounts[supplier.id]) {
        supplierCounts[supplier.id].count += 1;
      } else {
        supplierCounts[supplier.id] = { ...supplier, count: 1 };
      }
    });
  });
  
  const topSuppliers = Object.values(supplierCounts)
    .sort((a, b) => b.count - a.count)
    .slice(0, 3);

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 space-y-8">
        {/* Header */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-1">
            GGRC Keten
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-black" data-testid="chain-title">
            Risico Overzicht
          </h1>
          <p className="text-sm text-neutral-600 mt-1">
            Visualisatie van de keten: Kwetsbaarheid → Bedreiging → Risico → Maatregel → Leverancier
          </p>
        </div>

        {/* Flow Legend */}
        <Card className="border-neutral-200 bg-neutral-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-center gap-2 flex-wrap text-sm">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-neutral-200">
                <AlertTriangle className="h-4 w-4 text-orange-500" strokeWidth={1.5} />
                <span className="text-neutral-700">Kwetsbaarheid</span>
              </div>
              <ArrowRight className="h-4 w-4 text-neutral-400" />
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-neutral-200">
                <Crosshair className="h-4 w-4 text-red-500" strokeWidth={1.5} />
                <span className="text-neutral-700">Bedreiging</span>
              </div>
              <ArrowRight className="h-4 w-4 text-neutral-400" />
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-neutral-200">
                <Shield className="h-4 w-4 text-purple-500" strokeWidth={1.5} />
                <span className="text-neutral-700">Risico</span>
              </div>
              <ArrowRight className="h-4 w-4 text-neutral-400" />
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-neutral-200">
                <CheckSquare className="h-4 w-4 text-blue-500" strokeWidth={1.5} />
                <span className="text-neutral-700">Maatregel</span>
              </div>
              <ArrowRight className="h-4 w-4 text-neutral-400" />
              <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-neutral-200">
                <Truck className="h-4 w-4 text-green-500" strokeWidth={1.5} />
                <span className="text-neutral-700">Leverancier</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-orange-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{safeStats.open_vulnerabilities}</p>
                  <p className="text-xs text-neutral-500">Open kwetsb.</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                  <Crosshair className="h-5 w-5 text-red-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{safeStats.total_threats}</p>
                  <p className="text-xs text-neutral-500">Bedreigingen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-purple-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{safeStats.open_risks}</p>
                  <p className="text-xs text-neutral-500">Open risico's</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <CheckSquare className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{safeStats.implemented_measures}</p>
                  <p className="text-xs text-neutral-500">Maatregelen klaar</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <Truck className="h-5 w-5 text-green-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-black">{safeStats.available_suppliers}</p>
                  <p className="text-xs text-neutral-500">Leveranciers</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top 3 Leveranciers */}
        {topSuppliers.length > 0 && (
          <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="h-5 w-5 text-amber-500" strokeWidth={1.5} />
                Top 3 Gekozen Leveranciers
              </CardTitle>
              <CardDescription>Leveranciers die het meest gekoppeld zijn aan risico's en maatregelen</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {topSuppliers.map((supplier, index) => (
                  <div 
                    key={supplier.id}
                    className={`p-4 rounded-lg border-2 ${
                      index === 0 ? 'border-amber-400 bg-amber-50' :
                      index === 1 ? 'border-neutral-300 bg-neutral-50' :
                      'border-amber-600/30 bg-amber-50/50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          index === 0 ? 'bg-amber-400 text-white' :
                          index === 1 ? 'bg-neutral-400 text-white' :
                          'bg-amber-600 text-white'
                        }`}>
                          <span className="text-lg font-bold">#{index + 1}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-black">{supplier.company_name}</p>
                          <p className="text-sm text-neutral-500">{supplier.count} koppeling{supplier.count > 1 ? 'en' : ''}</p>
                        </div>
                      </div>
                    </div>
                    {supplier.expertise?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {supplier.expertise.slice(0, 3).map((exp, i) => (
                          <Badge key={i} className="bg-green-100 text-green-700 border-transparent text-xs">
                            {exp}
                          </Badge>
                        ))}
                        {supplier.expertise.length > 3 && (
                          <Badge className="bg-neutral-100 text-neutral-600 border-transparent text-xs">
                            +{supplier.expertise.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-end">
                <Link to="/admin/leveranciers">
                  <Button variant="outline" className="border-neutral-200">
                    Alle leveranciers bekijken →
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Chain Visualization */}
        <div className="space-y-4">
          {chain.length > 0 ? (
            chain.map((item) => {
              const isExpanded = expandedRisks[item.risk.id];
              
              return (
                <Card 
                  key={item.risk.id}
                  className={`border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)] overflow-hidden transition-all ${
                    item.risk.severity === 'critical' ? 'border-l-4 border-l-red-500' :
                    item.risk.severity === 'high' ? 'border-l-4 border-l-orange-500' :
                    item.risk.severity === 'medium' ? 'border-l-4 border-l-yellow-500' :
                    'border-l-4 border-l-green-500'
                  }`}
                  data-testid={`chain-item-${item.risk.id}`}
                >
                  {/* Risk Header - Always Visible */}
                  <CardContent className="p-4">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleRiskExpand(item.risk.id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(item.risk.status)}
                          <span className="text-xs font-mono text-neutral-400">{item.risk.risk_id}</span>
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-black">{item.risk.title}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            {getSeverityBadge(item.risk.severity)}
                            <span className="text-sm text-neutral-500">Factor: {item.risk.factor}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {/* Quick Stats */}
                        <div className="hidden md:flex items-center gap-4 text-sm text-neutral-500">
                          <span className="flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4 text-orange-500" strokeWidth={1.5} />
                            {item.vulnerabilities.length}
                          </span>
                          <span className="flex items-center gap-1">
                            <CheckSquare className="h-4 w-4 text-blue-500" strokeWidth={1.5} />
                            {item.measures.length}
                          </span>
                          {item.suppliers.length > 0 && (
                            <span className="flex items-center gap-1">
                              <Truck className="h-4 w-4 text-green-500" strokeWidth={1.5} />
                              {item.suppliers.length}
                            </span>
                          )}
                        </div>
                        <Button variant="ghost" size="sm">
                          {isExpanded ? (
                            <ChevronUp className="h-5 w-5" strokeWidth={1.5} />
                          ) : (
                            <ChevronDown className="h-5 w-5" strokeWidth={1.5} />
                          )}
                        </Button>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="mt-6 space-y-6">
                        {/* Chain Flow */}
                        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                          {/* Vulnerabilities */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-neutral-600">
                              <AlertTriangle className="h-4 w-4 text-orange-500" strokeWidth={1.5} />
                              Kwetsbaarheden ({item.vulnerabilities.length})
                            </div>
                            {item.vulnerabilities.length > 0 ? (
                              <div className="space-y-2">
                                {item.vulnerabilities.map((vuln) => (
                                  <div key={vuln.id} className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-mono text-orange-600">{vuln.vuln_id}</span>
                                      <Badge className="bg-orange-100 text-orange-700 border-transparent text-xs capitalize">
                                        {vuln.domain}
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-neutral-700 mt-1 line-clamp-2">{vuln.title}</p>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-neutral-400 p-3 bg-neutral-50 rounded-lg">
                                Geen kwetsbaarheden gekoppeld
                              </p>
                            )}
                          </div>

                          {/* Threat */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-neutral-600">
                              <Crosshair className="h-4 w-4 text-red-500" strokeWidth={1.5} />
                              Bedreiging
                            </div>
                            {item.threat ? (
                              <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-mono text-red-600">{item.threat.threat_id}</span>
                                </div>
                                <p className="text-sm text-neutral-700 mt-1">{item.threat.title}</p>
                                <div className="flex items-center gap-2 mt-2 text-xs text-red-600">
                                  <span>Kans: {item.threat.probability}/5</span>
                                  <span>Impact: {item.threat.impact_avg}/5</span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-neutral-400 p-3 bg-neutral-50 rounded-lg">
                                Geen bedreiging gekoppeld
                              </p>
                            )}
                          </div>

                          {/* Measures */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-neutral-600">
                              <CheckSquare className="h-4 w-4 text-blue-500" strokeWidth={1.5} />
                              Maatregelen ({item.measures.length})
                            </div>
                            {item.measures.length > 0 ? (
                              <div className="space-y-2">
                                {item.measures.map((measure) => (
                                  <div key={measure.id} className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-mono text-blue-600">{measure.measure_id}</span>
                                      {getMeasureStatusBadge(measure.status)}
                                    </div>
                                    <p className="text-sm text-neutral-700 mt-1 line-clamp-2">{measure.name}</p>
                                    {measure.supplier && (
                                      <div className="flex items-center gap-1 mt-2 text-xs text-green-600">
                                        <Truck className="h-3 w-3" />
                                        {measure.supplier.company_name}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-neutral-400 p-3 bg-neutral-50 rounded-lg">
                                Geen maatregelen gekoppeld
                              </p>
                            )}
                          </div>

                          {/* Suppliers */}
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-neutral-600">
                              <Truck className="h-4 w-4 text-green-500" strokeWidth={1.5} />
                              Leveranciers ({item.suppliers.length})
                            </div>
                            {item.suppliers.length > 0 ? (
                              <div className="space-y-2">
                                {item.suppliers.map((supplier) => (
                                  <div key={supplier.id} className="p-3 bg-green-50 rounded-lg border border-green-100">
                                    <p className="text-sm font-medium text-neutral-700">{supplier.company_name}</p>
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {supplier.expertise?.map((exp, i) => (
                                        <Badge key={i} className="bg-green-100 text-green-700 border-transparent text-xs">
                                          {exp}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="p-3 bg-neutral-50 rounded-lg">
                                <p className="text-sm text-neutral-400">Geen leveranciers</p>
                                <Link to="/leveranciers">
                                  <Button variant="link" className="p-0 h-auto text-sm text-black">
                                    Vind een leverancier →
                                  </Button>
                                </Link>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card className="border-neutral-200 shadow-[0_2px_4px_rgba(0,0,0,0.04)]">
              <CardContent className="p-12 text-center">
                <Shield className="h-12 w-12 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-lg font-medium text-black mb-2">Geen risico's gedefinieerd</h3>
                <p className="text-sm text-neutral-500 mb-4">
                  Begin met het identificeren van kwetsbaarheden en risico's om de GGRC keten te visualiseren.
                </p>
                <div className="flex items-center justify-center gap-4">
                  <Link to="/vulnerabilities">
                    <Button variant="outline" className="border-neutral-200">
                      <AlertTriangle className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Kwetsbaarheden
                    </Button>
                  </Link>
                  <Link to="/risks">
                    <Button className="bg-black text-white hover:bg-neutral-800">
                      <Shield className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Risico's toevoegen
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default GGRCChainPage;
